import { useCallback, useEffect, useRef, useState } from "react";

export type UseMinLoadingOptions = {
  minMs?: number;
};

export type UseMinLoadingResult = {
  loading: boolean;
  run: <T>(action: () => Promise<T>) => Promise<T>;
};

function waitMs(ms: number): Promise<void> {
  if (ms <= 0) return Promise.resolve();
  return new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });
}

export function useMinLoading(
  options: UseMinLoadingOptions = {},
): UseMinLoadingResult {
  const minMs = options.minMs ?? 1000;
  const [loading, setLoading] = useState(false);

  const mountedRef = useRef(true);
  const runSeqRef = useRef(0);
  const inFlightRef = useRef<Promise<unknown> | null>(null);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const run = useCallback(
    async <T>(action: () => Promise<T>): Promise<T> => {
      if (inFlightRef.current) {
        return inFlightRef.current as Promise<T>;
      }

      const seq = runSeqRef.current + 1;
      runSeqRef.current = seq;
      const startedAt = Date.now();

      if (mountedRef.current) {
        setLoading(true);
      }

      const promise = (async () => {
        try {
          const result = await action();
          const elapsed = Date.now() - startedAt;
          await waitMs(minMs - elapsed);
          return result;
        } finally {
          inFlightRef.current = null;
          if (mountedRef.current && runSeqRef.current === seq) {
            setLoading(false);
          }
        }
      })();

      inFlightRef.current = promise;
      return promise;
    },
    [minMs],
  );

  return { loading, run };
}
